import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  bg0,
  bgBlue,
  bgPrimaryText,
  text0,
  text3,
  text4,
} from '@osmosis/tailwind-ui/styles';
import { aiChatGradientBorderStyle } from './searchStyles';

type ActionsProps = {
  onClear: () => void;
  onSubmit: () => void;
};

const MAX_CHARS = 1500;

const AiChatInputActions = ({ onClear, onSubmit }: ActionsProps) => {
  const intl = useIntl();

  const clearLabel = intl.formatMessage({
    id: 'common.clear',
    defaultMessage: 'Clear',
  });

  const submitLabel = intl.formatMessage({
    id: 'common.submit',
    defaultMessage: 'Submit',
  });

  return (
    <Box data-testid="ai-chat-input-actions" className="min-w-auto">
      <Flex className="items-center">
        <Button
          data-testid="ai-chat-input-clear"
          tabIndex={0}
          lang={intl.locale}
          aria-label={clearLabel}
          className={cn(
            text3,
            `p-0.5 mr-2 text-xl hover:${text4} rounded-md bg-transparent hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`,
          )}
          onClick={onClear}
          variant="transparent"
          onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              onClear();
            }
          }}
        >
          <Icon icon={faTimes} aria-hidden="true" />
        </Button>
        <Box className="mx-1" data-testid="ai-chat-input-submit-container">
          <Button
            lang={intl.locale}
            aria-labelledby="ai-chat-input-submit-label"
            aria-label={submitLabel}
            data-testid="ai-chat-input-submit"
            className={cn(
              bgBlue,
              text0,
              `h-[35px] rounded-md px-4 hover:${bgPrimaryText}`,
            )}
            onClick={onSubmit}
          >
            <span id="ai-chat-input-submit-label" className="not-sr-only">
              <FormattedMessage
                id="dashboard.welcomeHeader.aiChatSubmitBtn"
                defaultMessage="Submit"
              />
            </span>
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

type Props = Readonly<{
  placeholder?: string;
  containerClasses?: string;
  dataTestId?: string;
  /** Icon shown at the start of the input. Defaults to the sparkles icon. */
  icon?: IconDefinition;
  ariaLabel?: string;
  focusOnMount?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onSubmit: (value: string) => void;
}>;

/**
 * Free-text input for asking Osmosis AI a question. Unlike Search, this has
 * no autocomplete/search-results wiring — submitting just hands the raw text
 * back to the caller via onSubmit.
 */
const AiChatInput = ({
  placeholder,
  containerClasses,
  dataTestId = 'ai-chat-input',
  icon = faSparkles,
  ariaLabel,
  focusOnMount,
  value: controlledValue,
  onChange,
  onSubmit,
}: Props) => {
  const intl = useIntl();
  const [internalValue, setInternalValue] = useState('');
  // Controlled when the caller passes value/onChange (e.g. to persist text across tab switches).
  const value = controlledValue ?? internalValue;
  const setValue = onChange ?? setInternalValue;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  const label =
    ariaLabel ??
    intl.formatMessage({
      id: 'dashboard.welcomeHeader.aiChatInput.ariaLabel',
      defaultMessage: 'Ask Osmosis AI a question',
    });

  const handleClear = () => setValue('');

  const handleSubmit = () => {
    if (!value.trim()) return;
    onSubmit(value);
    setValue('');
  };

  return (
    <Flex
      data-testid={dataTestId}
      className={cn('relative w-full items-center p-1', containerClasses)}
      style={aiChatGradientBorderStyle}
    >
      <Flex className="relative w-full items-center">
        <Box className="absolute left-4 top-1/2 z-10 -translate-y-1/2 transform">
          <Icon icon={icon} className={text3} />
        </Box>

        <Input
          ref={inputRef}
          data-testid={`${dataTestId}-input`}
          aria-label={label}
          placeholder={placeholder}
          value={value}
          maxLength={MAX_CHARS}
          onChange={(e) => setValue(e.target.value)}
          onEnter={handleSubmit}
          cn={cn(
            'pl-9 h-9 w-full rounded-md border-none focus:outline-none',
            bg0,
            `placeholder:${text4}`,
          )}
        />
      </Flex>

      {value.length > 0 && (
        <AiChatInputActions onClear={handleClear} onSubmit={handleSubmit} />
      )}
    </Flex>
  );
};

export default AiChatInput;
